import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useChangePassword } from "@/api/auth/mutations";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/auth-context";
import { createPasswordSchema } from "@/lib/schemas/auth.schema";
import { setDrfErrors } from "@/utils/errors";

type FormValues = {
	old_password: string;
	new_password: string;
	confirm_new_password: string;
};

export function ChangePasswordTab() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const changePassword = useChangePassword();

	const schema = z
		.object({
			old_password: z.string().min(1, "required"),
			new_password: createPasswordSchema({ username: user?.username }),
			confirm_new_password: z.string(),
		})
		.refine((d) => d.new_password === d.confirm_new_password, {
			path: ["confirm_new_password"],
			message: "passwordMismatch",
		});

	const {
		register,
		handleSubmit,
		reset,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			old_password: "",
			new_password: "",
			confirm_new_password: "",
		},
	});

	function onSubmit(values: FormValues) {
		changePassword.mutate(values, {
			onSuccess: () => {
				toast.success(t("settings.password.changed"));
				reset();
			},
			onError: (error) => {
				setDrfErrors(error, setError);
				toast.error(t("settings.password.error"));
			},
		});
	}

	const newPasswordError = errors.new_password?.message;
	const confirmError = errors.confirm_new_password?.message;

	return (
		<Card>
			<CardContent className="flex flex-col gap-4 pt-6">
				<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
					<div className="grid gap-2">
						<Label htmlFor="current-password">
							{t("settings.password.current")}
						</Label>
						<Input
							id="current-password"
							type="password"
							autoComplete="current-password"
							{...register("old_password")}
							aria-invalid={!!errors.old_password}
						/>
						{errors.old_password?.message && (
							<p className="text-destructive text-sm" role="alert">
								{errors.old_password.message}
							</p>
						)}
					</div>
					<div className="grid gap-2">
						<Label htmlFor="new-password">{t("settings.password.new")}</Label>
						<Input
							id="new-password"
							type="password"
							autoComplete="new-password"
							{...register("new_password")}
							aria-invalid={!!newPasswordError}
						/>
						{newPasswordError && (
							<p className="text-destructive text-sm" role="alert">
								{t(`settings.password.${newPasswordError}`, {
									defaultValue: newPasswordError,
								})}
							</p>
						)}
					</div>
					<div className="grid gap-2">
						<Label htmlFor="confirm-password">
							{t("settings.password.confirm")}
						</Label>
						<Input
							id="confirm-password"
							type="password"
							autoComplete="new-password"
							{...register("confirm_new_password")}
							aria-invalid={!!confirmError}
						/>
						{confirmError && (
							<p className="text-destructive text-sm" role="alert">
								{t(`settings.password.${confirmError}`, {
									defaultValue: confirmError,
								})}
							</p>
						)}
					</div>
					<div className="flex justify-end">
						<Button
							type="submit"
							size="sm"
							disabled={changePassword.isPending || isSubmitting}
						>
							{changePassword.isPending
								? t("settings.password.changing")
								: t("settings.password.change")}
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	);
}
